import { lazy, useEffect, useMemo, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  CfoInsightsResponse,
  InsightCard,
  MetricComparisonKind,
  QuickBooksFpaForecastLineMetadata,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { CfoInsightCards } from './CfoInsightCards'
import { CfoDepthSection } from './CfoDepthSection'
import { ScenarioLines } from './charts'
import { CfoAiRead, CfoChartSkeleton, CfoControlRow, CfoSegmentedControl } from './CfoPagePrimitives'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import {
  scenarioBundleFromConfidenceBand,
  scenarioBundleFromScenarioLines,
  type ForecastScenarioKey,
  type ScenarioBundle,
} from './forecastScenarios'
import { formatAsOf, formatSignedCurrency, trimStr } from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import { cfoProFeaturesAvailable, useCfoBootstrap } from './useCfoBootstrap'
import { CfoProUpgradeNotice } from './CfoProUpgradeNotice'
import type { CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard } from './analysis/types'

const QuickBooksForecastInsightsWorkspace = lazy(() =>
  import('../QuickBooksForecastInsightsWorkspace').then((m) => ({ default: m.QuickBooksForecastInsightsWorkspace })),
)

const SURFACE = CFO_SURFACES.scenarios
type ScenarioView = 'base' | 'upside' | 'downside' | 'new'

const scenarioFiniteNumber = (value: unknown): number | null => {
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

const scenarioSourceFingerprint = (bundle: ScenarioBundle | null, asOfLabel: string): string =>
  [
    SURFACE,
    trimStr(bundle?.source || ''),
    trimStr(asOfLabel),
    scenarioFiniteNumber(bundle?.baseEnd) ?? 0,
    scenarioFiniteNumber(bundle?.upsideEnd) ?? 0,
    scenarioFiniteNumber(bundle?.downsideEnd) ?? 0,
    bundle?.series?.map((series) => `${series.key}:${series.points.length}:${series.points.at(-1) ?? 0}`).join('|') || '',
  ].join(':')

const scenarioTrend = (bundle: ScenarioBundle) => {
  const baseSeries = bundle.series.find((series) => series.key === 'base') || bundle.series[0]
  const points = (baseSeries?.points || []).slice(-12).map((value, index, all) => ({
    label: index === all.length - 1 ? 'End' : `M-${all.length - index - 1}`,
    rawValue: scenarioFiniteNumber(value),
    value: formatSignedCurrency(scenarioFiniteNumber(value)),
  }))
  return {
    label: points.length >= 12 ? '12-month scenario path' : 'Available scenario path',
    points,
    unavailable_reason: points.length >= 12 ? null : `Scenario bundle has ${points.length} forecast points for the base path.`,
  }
}

const scenarioComparisonRows = () => [
  { label: 'Current month', current: '-', prior: '-', change: '-', tone: 'neutral', unavailableReason: 'Scenario bundles are forward-looking and do not include actual historical comparison windows.' },
  { label: 'Last 3 months', current: '-', prior: '-', change: '-', tone: 'neutral', unavailableReason: 'Scenario bundles are forward-looking and do not include actual historical comparison windows.' },
  { label: 'YTD', current: '-', prior: '-', change: '-', tone: 'neutral', unavailableReason: 'Scenario bundles are forward-looking and do not include actual historical comparison windows.' },
  { label: 'Prior year', current: '-', prior: '-', change: '-', tone: 'neutral', unavailableReason: 'Scenario bundles are forward-looking and do not include actual historical comparison windows.' },
]

export const buildScenarioInstantCards = (args: {
  bundle: ScenarioBundle | null
  asOfLabel: string
  comparisonKind: MetricComparisonKind
}): CfoInstantCard[] => {
  const { bundle, asOfLabel, comparisonKind } = args
  if (!bundle) return []
  const spread = scenarioFiniteNumber(bundle.upsideEnd) != null && scenarioFiniteNumber(bundle.downsideEnd) != null
    ? Number(bundle.upsideEnd) - Number(bundle.downsideEnd)
    : null
  const sourceFingerprint = scenarioSourceFingerprint(bundle, asOfLabel)
  return [
    buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'scenarios',
        activeSection: SURFACE,
        period: asOfLabel,
        comparison: comparisonKind,
        asOfLabel,
        metricId: 'scenario_spread',
        metricLabel: 'Scenario spread',
        sourceComponent: 'scenario_bundle',
        sourceTables: ['forecast_package_scenarios'],
        grounding: null,
        instantCard: null,
        selection: {
          selection_key: 'scenarios:scenario_spread',
          value: spread ?? scenarioFiniteNumber(bundle.baseEnd),
          metric_delta: spread,
          source_fingerprint: sourceFingerprint,
          artifact_ids: ['forecast_scenario_bundle'],
          base_end: bundle.baseEnd,
          upside_end: bundle.upsideEnd,
          downside_end: bundle.downsideEnd,
          instant_card_trend: scenarioTrend(bundle),
          instant_card_comparisons: scenarioComparisonRows(),
          instant_card_insights: [
            spread != null
              ? `The scenario band spans ${formatSignedCurrency(spread)} between downside and upside by the end of the horizon.`
              : 'The scenario bundle shows the base path, but the full upside/downside spread is not available.',
          ],
          instant_card_prescriptive_actions: [
            'Inspect the driver assumptions creating the spread before editing the forecast or presenting a scenario.',
          ],
          instant_card_next_steps: [
            { kind: 'drilldown', label: 'Inspect scenario drivers', prompt: 'Show the driver assumptions behind the scenario spread.', target: { surface: SURFACE, metricId: 'scenario_spread' } },
            { kind: 'open_surface', label: 'Open Forecast', prompt: 'Open Forecast Studio with scenario context.', target: { surface: CFO_SURFACES.forecast, metricId: 'scenario_spread' } },
            { kind: 'compare', label: 'Compare scenarios', prompt: 'Compare base, upside, and downside paths deterministically.', target: { surface: SURFACE, metricId: 'scenario_spread' } },
          ],
          deterministic_read: spread != null
            ? `The scenario band spans ${formatSignedCurrency(spread)} from downside to upside by the end of the horizon.`
            : 'The scenario bundle shows the forecast path available for this workbook.',
        },
      },
      visual: null,
    }),
  ]
}

export type CfoScenariosPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

export function CfoScenariosPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoScenariosPageProps) {
  const [bundle, setBundle] = useState<ScenarioBundle | null>(null)
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [view, setView] = useState<ScenarioView>('base')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const { bootstrap, cacheState, analyticsReady, bootstrapLoading } = useCfoBootstrap(appApi, workbookId)
  // Scenarios (what-if modeling) is a Pro surface.
  const proFeatures = cfoProFeaturesAvailable(bootstrap)

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    if (!proFeatures) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    const loadAnalyticsSidecars = analyticsReady === true
    const loadScenarioBundle = async (): Promise<ScenarioBundle | null> => {
      const scenarios = ['base', 'upside', 'downside'] as const
      const responses = !bootstrap?.accounting_provider || bootstrap.accounting_provider === 'quickbooks'
        ? await Promise.all(scenarios.map((scenarioKey) =>
            appApi.getQuickBooksFpaMlForecastPackage(wb, {
              scenario_key: scenarioKey,
              limit: 2000,
            }),
          ))
        : await Promise.all(scenarios.map(async (scenarioKey) => {
            const forecast = await appApi.getCfoStatementForecast(wb, {
              scenario_key: scenarioKey,
              grain: 'month',
              statement_type: 'profit_and_loss',
              future_months: 12,
            })
            const revenue = forecast.statement?.rows?.find((row) => row.semantic_key === 'revenue' || row.node_id === 'revenue')
            const createdAt = forecast.package?.updated_at || new Date().toISOString()
            return {
              line_metadata: (forecast.statement?.periods || []).map((period, index) => ({
                id: `${scenarioKey}:revenue:${period}`,
                package_id: forecast.package?.id || `canonical:${scenarioKey}`,
                statement_type: 'profit_and_loss',
                node_id: 'revenue',
                period_start: period,
                period_grain: 'month',
                scenario_key: scenarioKey,
                forecast_basis: 'historical_trend',
                model_family: 'single_company_statistical',
                confidence: null,
                generated_value: revenue?.values?.[index] ?? null,
                final_value: revenue?.values?.[index] ?? null,
                created_at: createdAt,
              } satisfies QuickBooksFpaForecastLineMetadata)),
            }
          }))
      const linesByScenario = responses.reduce<Partial<Record<ForecastScenarioKey, QuickBooksFpaForecastLineMetadata[]>>>(
        (acc, response, index) => {
          acc[scenarios[index]] = response.line_metadata || []
          return acc
        },
        {},
      )
      return scenarioBundleFromScenarioLines(linesByScenario) || scenarioBundleFromConfidenceBand(linesByScenario.base)
    }
    const loadInsights = (appApi.getCfoInsights || appApi.getQuickBooksInsights)?.bind(appApi)
    Promise.all([
      loadScenarioBundle(),
      loadAnalyticsSidecars && typeof loadInsights === 'function'
        ? loadInsights(wb, { asOf: selectedEndPeriod, grain: 'month' }).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([loadedBundle, ins]) => {
        if (cancelled) return
        setBundle(loadedBundle)
        setInsights(ins)
      })
      .catch((err: any) => {
        if (cancelled) return
        setError(trimStr(err?.message || err) || 'Could not load scenarios.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [analyticsReady, appApi, bootstrap?.accounting_provider, proFeatures, selectedEndPeriod, workbookId])

  useEffect(() => {
    const canonicalAsOf = trimStr(insights?.as_of || '')
    if (!selectedEndPeriod && canonicalAsOf) onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [insights, onSelectedEndPeriodChange, selectedEndPeriod])

  const asOfLabel = useMemo(() => formatAsOf(selectedEndPeriod || insights?.as_of || null), [selectedEndPeriod, insights])
  const scenarioCards = useMemo<InsightCard[]>(
    () =>
      (insights?.cards || []).filter(
        (card) => card.drill_target.tab === 'forecast' && (card.drill_target.view === 'scenarios' || card.drill_target.view == null),
      ),
    [insights],
  )
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { openGlDetail, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts,
    onNavSelect,
    period: selectedEndPeriod || insights?.as_of || null,
  })
  const fromScenarioLines = bundle?.source === 'scenario_lines'
  const scenarioInstantCards = useMemo(
    () => buildScenarioInstantCards({ bundle, asOfLabel, comparisonKind }),
    [asOfLabel, bundle, comparisonKind],
  )
  const scenarioPageFactsFingerprint = useMemo(
    () => scenarioSourceFingerprint(bundle, asOfLabel),
    [asOfLabel, bundle],
  )

  const heroHeadline = bundle ? 'Three futures, one picture' : 'Scenario planning'
  const heroSubtext = bundle
    ? `The lines start together and fan out over the horizon — the spread is the story. Base lands near ${formatSignedCurrency(bundle.baseEnd ?? 0)}; the band runs from ${formatSignedCurrency(bundle.downsideEnd ?? 0)} to ${formatSignedCurrency(bundle.upsideEnd ?? 0)}.`
    : 'Compare base, upside, and downside paths side by side. Generate a forecast to populate the scenarios.'

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="scenarios"
      activeSection={SURFACE}
      eyebrow="Plan"
      title="Scenarios"
      lede={
        <>
          “Three futures for this year, side by side — <em>the spread is the story.</em>”
        </>
      }
      crumb={
        <>
          <b>Scenarios</b> · Compare
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={selectedEndPeriod || insights?.as_of || undefined}
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select CFO Core end period"
      comparisonLabel={comparisonOptions.find((option) => option.id === comparisonKind)?.label || 'vs. prior month'}
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) => onComparisonKindChange?.(value as MetricComparisonKind)}
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={cacheState}
      pageLoading={loading || bootstrapLoading}
      pageLoadingLabel="Loading scenario context…"
      copilotSubtitle="reading these scenarios"
      copilotPlaceholder="Ask about Scenarios…"
      copilotIntro="You’re on Scenarios. Ask me to change a driver, build a new scenario, or show what it takes to stay cash-flow positive."
      askPlaceholder="Ask about these scenarios…"
      suggestedPrompts={['What flips the year to a loss?', 'Which driver matters most?', 'Build a more conservative case']}
      instantCards={scenarioInstantCards}
      pageFactsFingerprint={scenarioPageFactsFingerprint}
      openGlDetail={openGlDetail}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {({ copilot }) => (
        !proFeatures ? (
          <CfoProUpgradeNotice appApi={appApi} workbookId={workbookId} title="Scenarios are part of Pro">
            Model the hire, the price change, the slow quarter — three futures side by side, with the spread as the story. Scenarios come with Pro.
          </CfoProUpgradeNotice>
        ) : (
        <>
          {glDetailModal}
          <CfoControlRow>
            <CfoSegmentedControl
              label="Scenario view"
              value={view}
              onChange={(value) => setView(value as ScenarioView)}
              options={[
                { id: 'base', label: 'Base' },
                { id: 'upside', label: 'Upside' },
                { id: 'downside', label: 'Downside' },
                { id: 'new', label: 'New' },
              ]}
            />
            <button type="button" className="cfo-shell-chip">Net income</button>
            <button type="button" className="cfo-shell-chip">12-month horizon</button>
          </CfoControlRow>

          <section className="hero rise">
            <div className="hero-top">
              <div>
                <h1>{heroHeadline}</h1>
                <p>{heroSubtext}</p>
                <span className="tag">
                  {fromScenarioLines
                    ? 'AI summary · from per-scenario forecast lines'
                    : 'AI summary · derived from your forecast confidence band'}
                </span>
              </div>
            </div>
            <figure className="figure">
              <div className="fig-head">
                <span className="fnum">Fig. 1</span>
                <span className="ftitle">Cumulative forecast — base · upside · downside</span>
                <span className="fmeta">{fromScenarioLines ? 'derived from scenario packages' : 'from the model confidence band'}</span>
              </div>
              {loading ? (
                <CfoChartSkeleton label="Loading scenario chart…" />
              ) : bundle ? (
                <ScenarioLines
                  className="fig-svg"
                  ariaLabel="Cumulative forecast across base, upside, and downside paths"
                  series={bundle.series}
                  axisLabels={bundle.axisLabels}
                />
              ) : (
                <p style={{ color: 'var(--muted)' }}>
                  No ML forecast is available yet, so there’s nothing to compare. Generate a forecast first.
                </p>
              )}
              <CfoAiRead>
                {fromScenarioLines
                  ? 'Each line is the cumulative path of the engine’s own base, upside, and downside scenario forecasts, read from the active forecast package with your line edits applied.'
                  : 'This package has no per-scenario forecast rows, so these lines come from the base line’s confidence band — base is the projection, upside the high band, downside the low band. Rerun Forecast ML to publish real scenario lines.'}
              </CfoAiRead>
            </figure>
          </section>

          <div className="section-title rise">
            <h2 className="ff-guide">What each scenario assumes</h2>
            <span className="meta">ranked by likelihood</span>
          </div>
          <CfoInsightCards
            cards={scenarioCards}
            loading={loading}
            error={error}
            copilot={copilot}
            surface={SURFACE}
            workbookId={workbookId}
          />

          <CfoDepthSection
            title="Compare all scenarios"
            meta="The Forecast ML scenario workspace"
            showLabel="Open the scenario workspace"
            surface={SURFACE}
            workbookId={workbookId}
          >
            {() => (
              <QuickBooksForecastInsightsWorkspace appApi={appApi} workerClient={workerClient} workbookId={workbookId} />
            )}
          </CfoDepthSection>

          {classicEscapes ? (
            <button type="button" className="btn ghost" style={{ marginTop: 'var(--s4)' }} onClick={onOpenClassic}>
              Open forecast scenarios
            </button>
          ) : null}
        </>
        )
      )}
    </CfoInteriorPage>
  )
}

export default CfoScenariosPage
